import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { CallState } from '@lcs/shared/api';
import { Store, select } from '@ngrx/store';
import { Observable } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { Detail } from 'src/app/common-components/detail/detail';
import { FormsBase } from 'src/app/common-components/forms-base/forms-base';
import {
   getExtensionPrivileges,
   getMailboxPrivileges,
   getPromptPrivileges,
} from 'src/app/privileges/privileges.selector';
import { AddNewPromptEvent } from '../../extension-details/models/add-new-prompt-event.model';
import { ExtensionPrivileges } from '../../extension-details/models/extension-privileges';
import { clearMailbox, resetMailbox } from '../../mailbox-messages/state/mailbox-message-detail.actions';
import { MailboxPrivileges } from '../../mailbox-register/models/mailbox-privileges';
import { PromptPrivileges } from '../../prompt-register/models/prompt-privileges';
import { MailboxDetailSaveEvent } from '../models/mailbox-detail-form-submit-event';
import { MailboxDetailView } from '../models/mailbox-detail-view.model';
import { MailboxDetailFormService } from '../services/mailbox-detail-form.service';
import * as MailboxDetailActions from '../state/mailbox-detail.actions';
import { getMailbox, getMailboxCallState } from '../state/mailbox-detail.selectors';

@Component({
    encapsulation: ViewEncapsulation.None,
    host: { class: 'entity-detail' },
    providers: [MailboxDetailFormService, { provide: FormsBase, useExisting: MailboxDetailFormService }],
    selector: 'ndt-mailbox-standalone-detail',
    templateUrl: './mailbox-standalone-detail.component.html',
    standalone: false
})
export class MailboxDetailStandaloneComponent extends Detail<MailboxDetailFormService> implements OnDestroy, OnInit {
   mailbox$: Observable<MailboxDetailView | null>;

   callState$: Observable<CallState>;

   mailboxPrivileges$: Observable<MailboxPrivileges>;

   extensionPrivileges$: Observable<ExtensionPrivileges>;

   promptPrivileges$: Observable<PromptPrivileges>;

   constructor(public store: Store, private router: Router, protected formService: MailboxDetailFormService) {
      super(store, formService);
      this.mailbox$ = this.store.pipe(select(getMailbox));
      this.callState$ = this.store.pipe(select(getMailboxCallState));
      this.mailboxPrivileges$ = this.store.pipe(select(getMailboxPrivileges));
      this.extensionPrivileges$ = this.store.pipe(select(getExtensionPrivileges));
      this.promptPrivileges$ = this.store.pipe(select(getPromptPrivileges));
      this.store.dispatch(MailboxDetailActions.mailboxDetailLoad());
   }

   ngOnInit(): void {
      this.router.events.pipe(takeUntil(this.destroyed$)).subscribe((e) => {
         if (e instanceof NavigationEnd) {
            this.store.dispatch(MailboxDetailActions.mailboxDetailLoad());
         }
      });
   }

   ngOnDestroy(): void {
      this.destroyed$.next();
   }

   onFormSaveCanceled(): void {
      this.store.dispatch(MailboxDetailActions.mailboxDetailCancel());
   }

   onFormSubmitted(event: MailboxDetailSaveEvent): void {
      this.store.dispatch(MailboxDetailActions.saveMailboxDetail(event));
   }

   onAddNewPrompt(event: AddNewPromptEvent): void {
      this.store.dispatch(
         MailboxDetailActions.addNewPrompt({
            promptType: event.promptType,
            voiceMailGreetingPrompt: event.voiceMailGreetingPrompt,
         })
      );
   }

   onEditMailboxPin(event: { mailboxId: number }): void {
      this.store.dispatch(MailboxDetailActions.editMailboxPin({ mailboxId: event.mailboxId }));
   }

   onClearMailbox(mailboxMessageIds: number[]) {
      this.store.dispatch(
         clearMailbox({
            mailboxMessageIds,
         })
      );
   }

   onResetMailbox(data: { mailboxId: number; mailboxMessageIds: number[] }) {
      this.store.dispatch(
         resetMailbox({
            mailboxId: data.mailboxId,
            mailboxMessageIds: data.mailboxMessageIds,
         })
      );
   }
}
